import { getDateTime } from '@/utils/dateTime';
import { IProvider } from '@/services/provider/provider.type';
import {
  COMMON_STEPPER_CONTENT,
  STATUS_CODES,
} from '@/constant/global/global.constant';
import { COMMON_STEPPER_BG_COLORS } from '@/constant/alerts/alerts.constant';
import { ESmartCoolerTransactionType } from './coolerTransaction.interface';

export const COOLER_TRANSACTION_LIMIT = 50;
export const COOLER_TRANSACTION_PAGE = 1;

export const COOLER_TRANSACTION_PROPS = {
  HEADING: 'Smart Cooler Transactions',
  SUB_HEADING: 'View and manage cooler transaction data',
  BUTTON: {
    VIEW_MESSAGE: 'VIEW',
    CLOSE_MESSAGE: 'CLOSE',
  },
  LABELS: {
    TRANSACTION_ID: 'Transaction ID',
    OPERATOR: 'Operator',
    OPERATOR_INFO: 'Operator Info',
    MARKET: 'Market',
    DEVICE_ID: 'Device ID',
    TYPE: 'Type',
    STATUS: 'Status',
    STATUS_CODE: 'Status Code',
    CREATED_ON: 'Created On',
    UPDATED_ON: 'Updated On',
  },
  FILTERS: {
    OPERATOR: 'Select Operator',
    PROVIDER: 'Select Provider',
    MARKET: 'Select Market',
    DEVICE: 'Select Device',
    TYPE: 'Select Type',
    STATUS: 'Select Status',
    STATUS_CODE: 'Select Status Code',
  },
};

// Type options for cooler transactions
export const COOLER_TRANSACTION_TYPES = [
  { id: 'ALL', name: 'All Types' },
  { id: 'UNLOCK', name: 'UNLOCK' },
  { id: 'RESTOCK', name: 'RESTOCK' },
  { id: 'TEST', name: 'TEST' },
  { id: 'TRAINING', name: 'TRAINING' },
  { id: 'LOCAL-UNLOCK', name: 'LOCAL-UNLOCK' },
];

// Status options
export const COOLER_TRANSACTION_STATUS = [
  { id: 'ALL', name: 'All Status' },
  { id: 'INPROGRESS', name: 'In Progress' },
  { id: 'FAILED', name: 'Failed' },
  { id: 'PROCESSED', name: 'Processed' },
  { id: 'INVALID', name: 'Invalid' },
];

// Status Code options
export const COOLER_TRANSACTION_STATUS_CODES = [
  { id: 'ALL', name: 'All Status Codes' },
  { id: '0', name: '0 - Success' },
  { id: '100', name: '100 - Processed' },
  { id: '400', name: '400 - Bad Request' },
  { id: '500', name: '500 - Server Error' },
];

// Expandable column for data object display
export interface ICoolerTransactionDataObject {
  key: string;
  value: string;
}

// Color coding for status codes (similar to VDI transaction)
export const getStatusCodeColor = (statusCode: number): string => {
  if (statusCode === 0 || statusCode === 100) {
    return 'green';
  }
  if (statusCode >= 500) {
    return 'amber';
  }
  return 'red';
};

// Color coding for transaction types
export const getTransactionTypeColor = (type: string): string => {
  switch (type) {
    case ESmartCoolerTransactionType.RESTOCK:
      return 'blue';
    case ESmartCoolerTransactionType.TRAINING:
      return 'purple';
    case ESmartCoolerTransactionType.UNLOCK:
      return 'green';
    case ESmartCoolerTransactionType.TEST:
      return 'amber';
    case 'LOCAL-UNLOCK':
    case 'LOCALUNLOCK':
      return 'cyan';
    default:
      return 'gray';
  }
};

export const SMART_COOLER_STEPPER_ORDER = ['VMS', 'GATEWAY', 'MMS', 'CLIENTS'];
export const smartCoolerTransactionsConfig = (
  providersData: IProvider[],
  partnerIdFromResponse: string,
) => {
  // Getting Alerts Config Object from Providers Data Config Object
  const smartCoolerConfigFromProvider = providersData?.find(
    (provider) => provider?.partnerId === partnerIdFromResponse,
  )?.configs?.VDI_SETTINGS?.SMART_COOLERS;

  if (!smartCoolerConfigFromProvider) {
    return {
      smartCoolerConfigFromProvider: null,
      ENABLED_SMART_COOLER_STEPS: [],
    };
  }
  // 🔥 Fixed step order

  const ENABLED_SMART_COOLER_STEPS: { stepTitle: string; label: any }[] = [];
  const EXCLUDED_SMART_COOLER_STEPS: { stepTitle: string; label: any }[] = [];

  SMART_COOLER_STEPPER_ORDER.forEach((key) => {
    const value = smartCoolerConfigFromProvider[key];

    if (value?.ENABLED) {
      ENABLED_SMART_COOLER_STEPS.push({
        stepTitle: key,
        label: value?.AUTH?.AUTH_TYPE?.toUpperCase(),
      });
    } else {
      EXCLUDED_SMART_COOLER_STEPS.push({
        stepTitle: key,
        label: value?.AUTH?.AUTH_TYPE?.toUpperCase(),
      });
    }
  });
  return {
    smartCoolerConfigFromProvider,
    ENABLED_SMART_COOLER_STEPS,
    EXCLUDED_SMART_COOLER_STEPS,
    SMART_COOLER_STEPPER_ORDER,
  };
};
export type coolerTransactionActionStatus = Record<
  string,
  boolean | 'Not Started'
>;

interface ServerStatus {
  actionStatus: coolerTransactionActionStatus;
  message?: string | null;
  statusCode?: number;
  server?: string;
  success?: boolean;
  activityDtm?: string;
  partialSuccess?: boolean;
}
export interface ICoolerTransactionErrorProps {
  errorId: string;
  transactionId: string;
  objectName: string;
  statusCode: number;
  statusMessage: string;
  createdOn: string;
  updatedOn: string;
}
export interface coolerTransactionsActivity {
  action: string;
  server: string;
  statusCode: number;
  activityDtm: string;
  message?: string;
  success: boolean;
}

type CustomisedStepperResponse = Record<string, ServerStatus> & {
  eachStepSummary: Record<string, coolerTransactionActionStatus>;
};

const { STEPPER_TITLES, ACTIONS } = COMMON_STEPPER_CONTENT;

export function parseCoolerTransactionStepperData(
  allowedSteps: { stepTitle: string; label: string }[],
  activity: coolerTransactionsActivity[],
): CustomisedStepperResponse {
  // Dynamic Placeholder Object for Each step - Action Status.
  const coolerTransactionStepperProgress: Record<string, ServerStatus> = {
    [STEPPER_TITLES.GATEWAY]: {
      actionStatus: {
        [ACTIONS.GATEWAY_STEP_ACTIONS.CREATE]: ACTIONS.STATUS.NOT_STARTED,
        [ACTIONS.GATEWAY_STEP_ACTIONS.UPDATE]: ACTIONS.STATUS.NOT_STARTED,
      },
    },
    [STEPPER_TITLES.MMS]: {
      actionStatus: {
        [ACTIONS.MMS_STEP_ACTIONS.CREATE_SYNC]: ACTIONS.STATUS.NOT_STARTED,
        [ACTIONS.MMS_STEP_ACTIONS.UPDATE_SYNC]: ACTIONS.STATUS.NOT_STARTED,
      },
    },
    [STEPPER_TITLES.CLIENTS]: {
      actionStatus: {
        [ACTIONS.CLIENT_STEP_ACTIONS.CREATE_SYNC]: ACTIONS.STATUS.NOT_STARTED,
        [ACTIONS.CLIENT_STEP_ACTIONS.GET_SYNC]: ACTIONS.STATUS.NOT_STARTED,
        [ACTIONS.CLIENT_STEP_ACTIONS.UPDATE_SYNC]: ACTIONS.STATUS.NOT_STARTED,
      },
    },
  };

  try {
    // Safely access the alertLog array, defaulting to an empty array
    // if `logs` or `logs.alertLog` are not present or not an array.
    const coolerTransactionActivityLogs: coolerTransactionsActivity[] =
      Array.isArray(activity) ? activity : [];

    const sortedCoolerTransactionActions: coolerTransactionsActivity[] = [
      ...coolerTransactionActivityLogs,
    ].sort((a, b) => {
      const dateA = new Date(a?.activityDtm)?.getTime();
      const dateB = new Date(b?.activityDtm)?.getTime();
      // Handle invalid dates by returning 0, which keeps their relative order.
      return (isNaN(dateA) ? 0 : dateA) - (isNaN(dateB) ? 0 : dateB);
    });
    for (const coolerTransactionAction of sortedCoolerTransactionActions) {
      // Use optional chaining to safely access properties of the alert object.
      const { action, server, statusCode, activityDtm, message, success } =
        coolerTransactionAction || {};
      const serverValueOfAlertAction = server?.toUpperCase();
      const stepLabelWithMatchingServer = allowedSteps?.find(
        (step: any) => step?.label?.toUpperCase() === serverValueOfAlertAction,
      );
      const serverKey = stepLabelWithMatchingServer?.stepTitle;

      // Ensure serverKey is a defined string before using it to index the object.
      if (
        typeof serverKey !== 'string' ||
        !coolerTransactionStepperProgress[serverKey]
      ) {
        console.warn(
          `Skipping invalid log entry due to unknown or missing server: ${String(serverKey)}`,
        );
        continue;
      }

      const serverObject = coolerTransactionStepperProgress[serverKey];

      // Update latest event details
      serverObject.message =
        message ||
        (statusCode > STATUS_CODES.CREATED
          ? `Failed During ${action} - with statusCode - ${statusCode}`
          : null);
      serverObject.activityDtm = activityDtm;
      serverObject.statusCode = statusCode;
      // Update sub-step statuses without overwriting previous states

      if (serverKey === STEPPER_TITLES.GATEWAY) {
        if (action === ACTIONS.GATEWAY_STEP_ACTIONS.CREATE) {
          serverObject.actionStatus[ACTIONS.GATEWAY_STEP_ACTIONS.CREATE] =
            success;
        }
        if (action === ACTIONS.GATEWAY_STEP_ACTIONS.UPDATE) {
          serverObject.actionStatus[ACTIONS.GATEWAY_STEP_ACTIONS.UPDATE] =
            success;
        }
      } else if (serverKey === STEPPER_TITLES.MMS) {
        if (action === ACTIONS.MMS_STEP_ACTIONS.CREATE_SYNC) {
          serverObject.actionStatus[ACTIONS.MMS_STEP_ACTIONS.CREATE_SYNC] =
            success;
        }
        if (action === ACTIONS.MMS_STEP_ACTIONS.UPDATE_SYNC) {
          serverObject.actionStatus[ACTIONS.MMS_STEP_ACTIONS.UPDATE_SYNC] =
            success;
        }
      } else if (serverKey === STEPPER_TITLES.CLIENTS) {
        if (action === ACTIONS.CLIENT_STEP_ACTIONS.CREATE_SYNC) {
          serverObject.actionStatus[ACTIONS.CLIENT_STEP_ACTIONS.CREATE_SYNC] =
            success;
        }
        if (action === ACTIONS.CLIENT_STEP_ACTIONS.GET_SYNC) {
          serverObject.actionStatus[ACTIONS.CLIENT_STEP_ACTIONS.GET_SYNC] =
            success;
        }
        if (action === ACTIONS.CLIENT_STEP_ACTIONS.UPDATE_SYNC) {
          serverObject.actionStatus[ACTIONS.CLIENT_STEP_ACTIONS.UPDATE_SYNC] =
            success;
        }
      }
    }
  } catch (e) {
    console.error('An error occurred while processing the logs:', e);
    // The default object will be returned below.
  }

  // Final calculations for success and partialSuccess after all events are processed
  for (const server in coolerTransactionStepperProgress) {
    if (
      Object.prototype.hasOwnProperty.call(
        coolerTransactionStepperProgress,
        server,
      ) &&
      server !== 'eachStepSummary'
    ) {
      const subSteps = coolerTransactionStepperProgress[server].actionStatus;
      const allStepsComplete = Object.values(subSteps).every(
        (status) => status === true,
      );
      const someStepsComplete = Object.values(subSteps).some(
        (status) => status === true,
      );

      coolerTransactionStepperProgress[server].success = allStepsComplete;
      coolerTransactionStepperProgress[server].partialSuccess =
        !allStepsComplete && someStepsComplete;
    }
  }
  const eachStepSummary: Record<string, coolerTransactionActionStatus> = {};
  for (const server in coolerTransactionStepperProgress) {
    if (
      Object.prototype.hasOwnProperty.call(
        coolerTransactionStepperProgress,
        server,
      )
    ) {
      eachStepSummary[server] =
        coolerTransactionStepperProgress[server].actionStatus;
    }
  }
  // Return the object with eachStepSummary added as a property
  return {
    ...coolerTransactionStepperProgress,
    eachStepSummary,
  } as CustomisedStepperResponse;
}

export const getCoolerStatusColor = (success?: boolean) => {
  if (success === true) return COMMON_STEPPER_BG_COLORS.SUCCESS; // Success
  if (success === false) return COMMON_STEPPER_BG_COLORS.FAILURE; // Failure
  return COMMON_STEPPER_BG_COLORS.NOT_STARTED; // Not Started
};

export const COOLER_TRANSACTION_STEPPER_CONTENT = {
  FIRST_STEP_SUCCESS_MESSAGE: 'Transaction Created',
};

export const COOLER_TRANSACTION_COLUMNS = {
  TRANSACTION_ID: 'Transaction ID',
  OPERATOR_PROVIDER: 'Operator / Provider',
  MARKET: 'Market ID',
  DEVICE_ID: 'Device ID',
  TYPE: 'Type',
  STATUS: 'Status',
  STATUS_CODE: 'Status Code',
  CREATED_ON: 'Created On',
  LAST_UPDATED: 'Updated On',
};
